import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, Download, RefreshCcw, Activity, UserX, ChevronDown, Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { productLabel } from '../applications/loanFieldConstants';

const ACCESS_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'restricted', label: 'View-Only', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'inactive', label: 'Inactive', color: 'bg-rose-100 text-rose-800 border-rose-200' },
];

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const d = (new Date() - new Date(iso)) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.floor(d));
  } catch { return null; }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
};

const BrokerReportPanel = ({ sharedSearch = '', onSearchChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all'); // all | active | inactive
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | restricted | inactive
  const [savingId, setSavingId] = useState(null);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  // Read the shared search coming from AdminDashboard so there's only one
  // search box in the whole Users tab.
  const search = sharedSearch;

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats/brokers-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load broker report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const brokers = data?.brokers || [];
  const activeCount = data?.active_last_90d ?? 0;
  const inactiveCount = data?.inactive_last_90d ?? 0;
  const totalCount = data?.total_brokers ?? 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return brokers.filter((b) => {
      if (q) {
        const hay = `${b.name || ''} ${b.email || ''} ${b.company_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (activityFilter === 'active' && !b.active_last_90d) return false;
      if (activityFilter === 'inactive' && b.active_last_90d) return false;
      if (statusFilter !== 'all' && b.access_status !== statusFilter) return false;
      return true;
    });
  }, [brokers, search, activityFilter, statusFilter]);

  const updateAccessStatus = async (brokerId, newStatus) => {
    setSavingId(brokerId);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${brokerId}/access-status`,
        { access_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Optimistic update
      setData((prev) => ({
        ...prev,
        brokers: (prev?.brokers || []).map((b) => b.id === brokerId ? { ...b, access_status: newStatus } : b),
      }));
      toast.success(`Status updated to ${newStatus}`);
      setOpenStatusMenuId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingId(null);
    }
  };

  const exportCsv = () => {
    const header = ['Broker', 'Email', 'Company', 'Access Status', 'Programs Enabled', 'Total Submissions', 'Submissions by Program', 'Active in last 90 days', 'Last Activity'];
    const rows = filtered.map((b) => [
      b.name,
      b.email,
      b.company_name || '',
      b.access_status,
      (b.allowed_loan_types || []).map(productLabel).join('; '),
      String(b.total_submissions || 0),
      Object.entries(b.submissions_by_product || {}).map(([k, v]) => `${productLabel(k)}:${v}`).join('; '),
      b.active_last_90d ? 'Yes' : 'No',
      formatDate(b.last_activity_at),
    ]);
    const csv = [header, ...rows].map((r) => r.map((cell) => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `broker-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="broker-report-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-5 w-5 text-[#081120]" />
            Broker Report
            <span className="ml-2 text-xs font-normal text-slate-500">{totalCount} total · {activeCount} active · {inactiveCount} dormant (90d)</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="broker-report-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="broker-report-csv">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              CSV
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Search is shared with the Users tab filter bar below */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 flex-wrap">
          <div className="relative w-full md:w-72">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder="Search broker, email or company"
              className="pl-9 pr-8 h-9 text-sm"
              data-testid="broker-report-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearchChange?.('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="broker-report-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Activity:</span>
            {[
              { v: 'all', l: 'All', icon: null },
              { v: 'active', l: 'Active 90d', icon: <Activity className="h-3 w-3" /> },
              { v: 'inactive', l: 'Dormant 90d', icon: <UserX className="h-3 w-3" /> },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setActivityFilter(opt.v)}
                className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${
                  activityFilter === opt.v ? 'bg-[#081120] text-white border-[#081120]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`broker-report-activity-${opt.v}`}
              >
                {opt.icon}
                {opt.l}
              </button>
            ))}
            <span className="text-xs text-slate-500 ml-3">Access:</span>
            {[{ v: 'all', l: 'All' }, ...ACCESS_STATUSES.map((s) => ({ v: s.value, l: s.label }))].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setStatusFilter(opt.v)}
                className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                  statusFilter === opt.v ? 'bg-[#081120] text-white border-[#081120]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`broker-report-status-filter-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="broker-report-table">
            <thead>
              <tr className="text-left border-b border-slate-200 text-slate-600">
                <th className="py-2.5 pr-3 font-medium">Broker</th>
                <th className="py-2.5 pr-3 font-medium">Programs Enabled</th>
                <th className="py-2.5 pr-3 font-medium">Submissions by Program</th>
                <th className="py-2.5 pr-3 font-medium text-right">Total</th>
                <th className="py-2.5 pr-3 font-medium">Last Activity</th>
                <th className="py-2.5 pr-3 font-medium">90-Day</th>
                <th className="py-2.5 pr-3 font-medium">Access</th>
              </tr>
            </thead>
            <tbody>
              {loading && !filtered.length && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && !filtered.length && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500">No brokers match these filters.</td></tr>
              )}
              {filtered.map((b) => {
                const daysInactive = daysSince(b.last_activity_at);
                const statusMeta = ACCESS_STATUSES.find((s) => s.value === b.access_status) || ACCESS_STATUSES[0];
                return (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`broker-row-${b.id}`}>
                    <td className="py-3 pr-3 align-top">
                      <div className="font-medium text-slate-900">{b.name || '—'}</div>
                      <div className="text-xs text-slate-500">{b.email}</div>
                      {b.company_name && <div className="text-xs text-slate-400">{b.company_name}</div>}
                    </td>
                    <td className="py-3 pr-3 align-top">
                      {b.allowed_loan_types?.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {b.allowed_loan_types.slice(0, 6).map((t) => (
                            <Badge key={t} variant="outline" className="text-[10px] font-normal px-1.5 py-0.5">{productLabel(t)}</Badge>
                          ))}
                          {b.allowed_loan_types.length > 6 && (
                            <Badge variant="outline" className="text-[10px] font-normal px-1.5 py-0.5">+{b.allowed_loan_types.length - 6} more</Badge>
                          )}
                        </div>
                      ) : <span className="text-xs text-slate-400">None</span>}
                    </td>
                    <td className="py-3 pr-3 align-top">
                      {Object.keys(b.submissions_by_product || {}).length ? (
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {Object.entries(b.submissions_by_product)
                            .sort((a, b2) => b2[1] - a[1])
                            .map(([prod, count]) => (
                              <Badge key={prod} variant="secondary" className="text-[10px] font-medium px-1.5 py-0.5">
                                {productLabel(prod)}: {count}
                              </Badge>
                            ))}
                        </div>
                      ) : <span className="text-xs text-slate-400">0 submissions</span>}
                    </td>
                    <td className="py-3 pr-3 align-top text-right font-medium">{b.total_submissions || 0}</td>
                    <td className="py-3 pr-3 align-top">
                      <div className="text-xs text-slate-700">{formatDate(b.last_activity_at)}</div>
                      {daysInactive !== null && (
                        <div className="text-[10px] text-slate-400">{daysInactive}d ago</div>
                      )}
                    </td>
                    <td className="py-3 pr-3 align-top">
                      {b.active_last_90d ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-medium">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-medium">Dormant</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-3 align-top relative">
                      <button
                        type="button"
                        onClick={() => setOpenStatusMenuId(openStatusMenuId === b.id ? null : b.id)}
                        disabled={savingId === b.id}
                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border ${statusMeta.color} ${savingId === b.id ? 'opacity-60' : 'hover:opacity-80'}`}
                        data-testid={`broker-access-btn-${b.id}`}
                      >
                        {statusMeta.label}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {openStatusMenuId === b.id && (
                        <div
                          className="absolute right-3 mt-1 z-20 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[140px]"
                          data-testid={`broker-access-menu-${b.id}`}
                        >
                          {ACCESS_STATUSES.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => updateAccessStatus(b.id, opt.value)}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 ${opt.value === b.access_status ? 'font-semibold text-slate-900' : 'text-slate-700'}`}
                              data-testid={`broker-access-option-${b.id}-${opt.value}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-[11px] text-slate-500">
          <strong>Dormant</strong> = no new submission or app interaction in the last 90 days.
          <strong className="ml-3">Inactive</strong> access status refuses login entirely; <strong>View-Only</strong> allows read access only.
        </div>
      </CardContent>
    </Card>
  );
};

export default BrokerReportPanel;
